import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import moment from 'moment';

dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

export enum DateFormat {
  DEFAULT = 'DD/MM/YYYY',
  ISO = 'YYYY-MM-DD',
  FULL_DATE_TIME = 'YYYY-MM-DD HH:mm:ss',
  YEAR_MONTH_DAY_HOUR_MINUTE = 'YYYY/MM/DD HH:mm',
  SHORT_DATE = 'MM/DD/YYYY',
  TIME = 'HH:mm:ss',
  FULL_MONTH_DAY_YEAR = 'MMMM D, YYYY',
  MONTH_DAY_YEAR = 'MM-DD-YYYY',
  DAY_MONTH_YEAR = 'DD-MM-YYYY',
  YEAR_MONTH_DAY = 'YYYY/MM/DD',
  YEAR_MONTH_DAY_V2 = 'yy/mm/dd',
  DAY_MONTH_YEAR_TIME = 'DD-MM-YYYY HH:mm:ss',
  DAY_MONTH_TIME = 'MM/DD HH:mm',
  YEAR_MONTH_DAY_HOUR_MINUTE_SECOND = 'YYYY/MM/DD HH:mm:ss',
}

export const sourceTimeZone = 'UTC';

export const { timeZone } = Intl.DateTimeFormat().resolvedOptions();

/**
 * Formats a given date to the specified format.
 * @param {dayjs.Dayjs} date - The date to format.
 * @param {DateFormat} [format=DateFormat.DEFAULT] - The format string.
 * @returns {string} The formatted date string.
 */
export const formatDate = (date: dayjs.Dayjs, format: DateFormat = DateFormat.DEFAULT): string => {
  return dayjs(date).format(format);
};

/**
 * Calculates the duration between two dates.
 * @param {dayjs.Dayjs} startDate - The start date.
 * @param {dayjs.Dayjs} endDate - The end date.
 * @returns {string} The duration in days, hours, and minutes.
 */
export const calculateDuration = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs): string => {
  const diff = dayjs.duration(endDate.diff(startDate));
  return `${diff.days()} days, ${diff.hours()} hours, ${diff.minutes()} minutes`;
};

/**
 * Checks if a given date string is valid according to the specified format.
 * @param {string} date - The date string to check.
 * @param {DateFormat} [format=DateFormat.ISO] - The format string.
 * @returns {boolean} True if the date is valid, false otherwise.
 */
export const isValidDate = (date: string, format: DateFormat = DateFormat.ISO): boolean => {
  return dayjs(date, format, true).isValid();
};

/**
 * Converts a date to a specified timezone.
 * @param {dayjs.Dayjs} date - The date to convert.
 * @param {string} timezone - The target timezone.
 * @returns {string} The date string in the target timezone.
 */
export const convertTimezone = (date: dayjs.Dayjs, timezone: string, format: string = 'Z'): string => {
  return dayjs(date).tz(timezone).format(format);
};

/**
 * Gets the current date in 'YYYY-MM-DD' format.
 * @returns {string} The current date string.
 */
export const getCurrentDate = (): string => {
  return dayjs().format(DateFormat.ISO);
};

/**
 * Compares two dates.
 * @param {dayjs.Dayjs} date1 - The first date.
 * @param {dayjs.Dayjs} date2 - The second date.
 * @returns {number} -1 if date1 is before date2, 1 if date1 is after date2, 0 if they are equal.
 */
export const compareDates = (date1: dayjs.Dayjs, date2: dayjs.Dayjs): number => {
  if (date1.isBefore(date2)) {
    return -1;
  } else if (date1.isAfter(date2)) {
    return 1;
  }
  return 0;
};

/**
 * Gets the number of days in a specified month.
 * @param {number} year - The year.
 * @param {number} month - The month (1-12).
 * @returns {number} The number of days in the month.
 */
export const getDaysInMonth = (year: number, month: number): number => {
  return dayjs(`${year}-${month}-01`).daysInMonth();
};

/**
 * Converts a date value to 'YYYY/MM/DD' format if valid, otherwise returns the original value as string.
 * @param {any} value - The date value to format.
 * @returns {string} The formatted date string or original value.
 */
export const formatToYMD = (value: any): string => {
  if (value === null || value === undefined || value === '') return '-';
  // Date instance must be checked before generic object branch
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? '-' : moment(value).format(DateFormat.YEAR_MONTH_DAY);
  }
  // Support for object with label/value (e.g. dropdown items)
  let v = value;
  if (typeof value === 'object') {
    v = value.label || value.value || '';
  }
  if (v && moment(v).isValid()) {
    return moment(v).format(DateFormat.YEAR_MONTH_DAY);
  }
  return v ? String(v) : '-';
};

// Helper function to format date to ISO string with time
export const formatDateToISO = (date: Date | null): string | null => {
  if (!date) return null;
  return date.toISOString();
};

/**
 * Parse a 'YYYY-MM-DD' date string from API (interpreted as UTC midnight) into a Date object.
 * e.g. '2026-03-09' → new Date('2026-03-09T00:00:00Z')
 * @param dateStr - date string in 'YYYY-MM-DD' format
 * @param format - optional custom format string for parsing
 */
export const customFormatDate = (value: any, format?: string): string => {
  if (value === null || value === undefined || value === '') return '';
  // Support for object with label/value
  let v = value;
  if (typeof value === 'object') {
    v = value.label || value.value || '';
  }
  // Use moment for compatibility with existing code
  if (v && moment(v).isValid()) {
    return moment(v).format(format || DateFormat.YEAR_MONTH_DAY);
  }
  return v ? String(v) : '';
};
/**
 * Format a Date to a full ISO string for API payload.
 * Always returns midnight of that date in the target timezone.
 * e.g. new Date('2026-03-09T00:00:00Z')
 * @param date - Date object
 */
export const formatDateForApi = (date: Date | null | undefined): string | null => {
  if (!date) return null;
  return dayjs(date).toISOString();
};

export const formatUtcTimeToLocal = (dateTime: string, format: DateFormat) => {
  if (!dateTime) return '';
  return dayjs.tz(dateTime, sourceTimeZone).tz(timeZone).format(format);
};

export const toApiIsoEndOfDayUtc = (date: Date | null) => {
  if (!date) return '';
  const baseIso = formatDateForApi(date);
  if (!baseIso) return '';
  return moment.utc(baseIso).endOf('day').toISOString();
};
